import { DEFAULT_GROUP_BY_KEY, TABLE_ROW_ID } from "Consts";
import { isNullOrUndefined } from "./objectUtils";

const cleanArr: any = (arr = []) => {
  return (arr || []).filter((item) => item != null && item != undefined);
};

export const average = (numbersArr: number[]) => {
  const arr = cleanArr(numbersArr);
  if (!Array.isArray(arr) || arr.length === 0) {
    return null;
  }
  return arr.reduce((a, b) => a + b) / arr.length;
};

export const max = (numbersArr: number[]) => {
  const arr = cleanArr(numbersArr);

  if (!Array.isArray(arr) || arr.length === 0) {
    return null;
  }

  return Math.max(...arr);
};

export const sum = (numbersArr: number[]) => {
  const arr = cleanArr(numbersArr);
  if (!Array.isArray(arr) || arr.length === 0) {
    return null;
  }
  return arr.reduce((total, num) => total + num, 0);
};

export const getGroupByKey = (fieldVal: string | null, nullValueDefault = DEFAULT_GROUP_BY_KEY) => {
  const key = !fieldVal || fieldVal == "null" ? nullValueDefault : fieldVal;
  return key;
};

export const groupBy = (
  arr: any[] = [],
  field: string | ((dataRow: any) => string),
  nullValueDefault = DEFAULT_GROUP_BY_KEY
): { [key: string]: any } => {
  return (arr || []).reduce((result, dataRow) => {
    const groupValue: string = typeof field === "function" ? field(dataRow) : dataRow[field];
    const key = getGroupByKey(groupValue, nullValueDefault);

    (result[key] = result[key] || []).push(dataRow); // group by field ==> object list
    return result;
  }, {});
};

export const countBy = (
  arr: any[] = [],
  field: string | ((dataRow: any) => any),
  nullValueDefault = DEFAULT_GROUP_BY_KEY
): { [key: string]: number } => {
  const grouped = groupBy(arr, field, nullValueDefault);
  const result: { [key: string]: number } = {};
  Object.entries(grouped).forEach(([key, arr]) => {
    result[key] = arr.length;
  });

  return result;
};

export const getUniqueArray = (array = []) => {
  const s = new Set(array);
  return Array.from(s).sort();
};

export const getUniqueList = (array = [], field: string) => {
  const s = new Set(array.map((a) => a[field]));
  return Array.from(s).sort();
};

export const hasDuplicates = (array: any[] = []) => {
  return new Set(array).size !== array.length;
};

export const getUniqueSortedList = (array = [], field: string) => {
  const s = new Set(array.map((a) => a[field]));
  return Array.from(s).sort();
};

export const getUniqueSortedArrayItems = (array: any = []) => {
  const s = new Set(array);
  return Array.from(s).sort();
};

export const addRowNumber = (arr: any[], rowId = TABLE_ROW_ID) => {
  if (!arr || !Array.isArray(arr) || arr.length == 0) return arr;

  let counter = 0;
  arr.forEach((row) => {
    counter += 1;
    row[rowId] = counter;
  });

  return arr;
};

export const sortListByProperty = (arr: any[], prop: string, order: "asc" | "desc" = "desc") => {
  if (!Array.isArray(arr)) {
    return arr;
  }

  return order === "asc"
    ? arr.sort((a, b) => {
        if (a[prop]?.localeCompare) {
          return a[prop]?.toString()?.localeCompare(b[prop]?.toString());
        } else {
          return a[prop] > b[prop] ? 1 : -1;
        }
      })
    : arr.sort((a, b) => {
        if (b[prop]?.localeCompare) {
          return b[prop]?.toString()?.localeCompare(a[prop]?.toString());
        } else {
          return b[prop] > a[prop] ? 1 : -1;
        }
      });
};

export const mergeArraysByProperty = (a = [], b = [], prop: string) => {
  const reduced = a.filter((aitem) => !b.find((bitem) => aitem[prop] === bitem[prop]));
  return reduced.concat(b);
};

export const getFirstNonNullUndefined = (arr: any[] = []) => {
  return arr.find((item) => !isNullOrUndefined(item));
};

export const arrayIfEmpty = (arr: any[], fallbackArr: any[]) => {
  if (arr.length === 0) {
    return fallbackArr;
  }
  return arr;
};

export const arrayWithoutElementAtIndex = function (arr: any, index: number) {
  return (arr || []).filter((value: any, arrIndex: number) => index !== arrIndex);
};

export const getSelectedOnTop = (options: any, selected: any) => {
  return [
    ...(options?.length > 0
      ? [{ label: selected, checked: "on", ...options.find((i: any) => i.label === selected) }]
      : []),
    ...options
      .filter((i: any) => i.label !== selected)
      .sort((a: any, b: any) => a?.label?.localeCompare(b.label)),
  ];
};

const extractUtcOffsetMinutes = (timezoneText: string): number => {
  const normalized = timezoneText.replace(/\u2212/g, "-").trim();
  const utcMatch = normalized.match(/UTC\s*([+\-])\s*(\d{1,2})(?::(\d{2}))?(?::\d{2})?/i);
  if (!utcMatch) return 0;

  const sign = utcMatch[1] === "+" ? 1 : -1;
  const hours = parseInt(utcMatch[2], 10);
  const minutes = parseInt(utcMatch[3], 10);

  return sign * (hours * 60 + minutes);
};

export const sortTimezonesByUtcOffset = (timezones: Array<{ value: string; text: string }>) => {
  return [...timezones].sort((a, b) => {
    const offsetA = extractUtcOffsetMinutes(a.text);
    const offsetB = extractUtcOffsetMinutes(b.text);
    return offsetA - offsetB;
  });
};

export const chunkArray = (arr: any[], size: number) => {
  const result: any = [];
  for (let i = 0; i < arr.length; i += size) {
    result.push(arr.slice(i, i + size));
  }
  return result;
};

export const sortByFirstArrayItem = (a: any, b: any) => {
  const aStr = (a.name?.[0] || "").toLowerCase();
  const bStr = (b.name?.[0] || "").toLowerCase();

  const aFirst = aStr[0] || "";
  const bFirst = bStr[0] || "";

  if (aFirst < bFirst) return -1;
  if (aFirst > bFirst) return 1;

  // fallback to full string
  return aStr.localeCompare(bStr);
};

export const sortByLastArrayItem = (a: any, b: any) => {
  const aStr = (a.name?.[0] || "").toLowerCase();
  const bStr = (b.name?.[0] || "").toLowerCase();

  const aFirst = aStr[0] || "";
  const bFirst = bStr[0] || "";

  if (aFirst > bFirst) return -1;
  if (aFirst < bFirst) return 1;

  // fallback to full string
  return aStr.localeCompare(bStr);
};

type Column = {
  field: string;
  name: string;
};

const UNITS = ["Ms", "Kb", "Mb", "Gb", "Tb", "Bytes"];
const EXEPTIONS_UNITS = ["Ms", "Kb", "Bytes"];

const unit_mapping: any = {
  Ms: "sec",
  Bytes: "MB",
  Kb: "MB",
  Mb: "MB",
  Gb: "GB",
  Tb: "TB",
};

const millisecondsToSeconds = (ms: number): number => +(ms / 1000).toFixed(2);
const bytesToMegabytes = (bytes: number): number => +(bytes / (1024 * 1024)).toFixed(2);
const kilobytesToMegabytes = (kb: number): number => +(kb / 1024).toFixed(2);

const exeptionsRender: any = {
  Ms: millisecondsToSeconds,
  Bytes: bytesToMegabytes,
  Kb: kilobytesToMegabytes,
};

export const appendUnitToColumnName = (columns: Column[]): Column[] => {
  return columns.map((column) => {
    const unit = UNITS.find((unit) => column?.field?.endsWith(unit));

    return unit
      ? {
          ...column,
          name: `${column.name} (${unit_mapping?.[unit]})`,
          ...(EXEPTIONS_UNITS?.includes(unit) && {
            renderExcel: exeptionsRender?.[unit],
          }),
        }
      : column;
  });
};

export const mergeByName = (collection: any) => {
  // const map = new Map();

  // for (const item of collection) {
  //   if (map.has(item.name)) {
  //     const existing = map.get(item.name);
  //     const merged = [...existing.values, ...item.values];
  //     existing.values = merged.filter(
  //       (v, index, self) => index === self.findIndex((x) => x.value === v.value)
  //     );
  //     const { values, ...rest } = item;
  //     Object.assign(existing, rest);
  //   } else {
  //     map.set(item.name, { ...item, values: [...item.values] });
  //   }
  // }

  // return Array.from(map.values());

  const map = new Map();

  for (const item of collection) {
    if (map.has(item.name)) {
      const existing = map.get(item.name);
      const existingValues = existing.values;
      const incomingValues = item.values;

      const duplicateKeys = new Set(
        existingValues
          .filter((v: any) => incomingValues.some((x: any) => x.value === v.value))
          .map((v: any) => v.value)
      );

      // mark existing unique values
      existing.values = existingValues.map((v: any) =>
        duplicateKeys.has(v.value) ? v : { ...v, ownerType: item?.ownerType }
      );

      // add only incoming values that are not duplicates
      const uniqueIncoming = incomingValues
        .filter((v: any) => !duplicateKeys.has(v.value))
        .map((v: any) => ({ ...v, ownerType: item?.ownerType }));

      existing.values = [...existing.values, ...uniqueIncoming];

      const { values, ...rest } = item;
      Object.assign(existing, rest);
    } else {
      map.set(item.name, {
        ...item,
        values: [...item.values.map((v: any) => ({ ...v, ownerType: item?.ownerType }))],
      });
    }
  }

  return Array.from(map.values());
};
